import get from "lodash/get";

/**
 * Microsoft Clarity — Atlas integration utility.
 *
 * Responsibilities:
 *  1. Injects the Clarity script tag once (deduplication guard prevents double-load).
 *  2. Sets a `pageType` custom tag on every route transition so Product / UX can filter
 *     session recordings and heatmaps by module in the Clarity dashboard.
 *  3. Identifies the logged-in user so sessions are linkable to real users.
 *
 * Project ID resolution:
 *  - Read from globalThis._APP_CONFIG.clarityProjectId (injected server-side via template.ejs).
 *  - If absent the module exits silently — no script is injected.
 *
 * All automatic Clarity capabilities (session recordings, heatmaps, rage clicks,
 * dead clicks, scroll depth) activate as soon as the script loads.
 * No additional instrumentation is required for those features.
 */

/**
 * Maps URL path prefixes to human-readable page-type labels used in Clarity.
 * More-specific /dealer/* paths are listed first to ensure correct matching.
 */
const PAGE_TYPE_MAP = [
    // Specific /dealer/* sub-paths must precede the generic dealer catch
    { pattern: /^\/dealer\/warranty/, label: "Warranty" },
    { pattern: /^\/dealer\/supported-accessories/, label: "Accessories" },
    { pattern: /^\/dealer\/details/, label: "DealerDetails" },
    // All other modules
    { pattern: /^\/sales/, label: "Sales" },
    { pattern: /^\/leads/, label: "Leads" },
    { pattern: /^\/customers/, label: "Customers" },
    { pattern: /^\/vehicles/, label: "Vehicles" },
    { pattern: /^\/dealers/, label: "Dealers" },
    { pattern: /^\/settings/, label: "Settings" },
    { pattern: /^\/reporting/, label: "Reporting" },
    { pattern: /^\/prospects/, label: "Prospects" },
    { pattern: /^\/protection-products/, label: "ProtectionProducts" },
    { pattern: /^\/training/, label: "Training" },
    { pattern: /^\/sell-at-home/, label: "SellAtHome" },
    { pattern: /^\/import-logs/, label: "ImportLogs" },
    { pattern: /^\/pricing/, label: "VehiclePricing" },
    { pattern: /^\/applications/, label: "FinanceApplications" },
    { pattern: /^\/return-policy/, label: "ReturnPolicy" },
    { pattern: /^\/userSelfService/, label: "UserSelfService" },
    { pattern: /^\/widget-documentation/, label: "WidgetDocumentation" },
    { pattern: /^\/widget-customization/, label: "WidgetCustomization" },
    { pattern: /^\/walmart/, label: "Walmart" },
    { pattern: /^\/domo/, label: "Reporting" },
    { pattern: /^\/design-guide/, label: "DesignGuide" },
    { pattern: /^\/qaTools/, label: "QaTools" },
    // ConfigurationManager — has 4 distinct path prefixes
    { pattern: /^\/website-theming/, label: "WebsiteTheming" },
    { pattern: /^\/website-configuration/, label: "WebsiteConfiguration" },
    { pattern: /^\/website-components/, label: "WebsiteComponents" },
    { pattern: /^\/marketing-settings/, label: "MarketingSettings" },
    // Remaining modules
    { pattern: /^\/payment-calculations/, label: "PaymentCalculations" },
    { pattern: /^\/trade-in-adjustment/, label: "TradeInAdjustment" },
];

let identifiedUserId;

/**
 * Returns a human-readable page-type label for the given path.
 * Falls back to "Other" when no pattern matches.
 * @param {string} path
 * @returns {string}
 */
export function resolvePageType(path) {
    for (const { pattern, label } of PAGE_TYPE_MAP) {
        if (pattern.test(path)) return label;
    }
    return "Other";
}

/**
 * Injects the Clarity `<script>` tag into <head> exactly once.
 * A deduplication guard (`globalThis['clarity-loaded']`) prevents double-load
 * when multiple Vue entrypoints are initialised on the same page.
 *
 * The queue stub is set up before the async script loads so any API calls
 * made in the interim are buffered and replayed by the real implementation.
 *
 * @param {string} projectId
 */
export function injectClarity(projectId) {
    // Guard 1: our own dedup flag
    if (globalThis["clarity-loaded"]) return;
    // Guard 2: real Clarity already on the page (e.g. loaded via GTM)
    //   Real Clarity replaces the stub so it won't have a `.q` array.
    if (typeof globalThis.clarity === "function" && !Array.isArray(globalThis.clarity.q)) return;
    globalThis["clarity-loaded"] = true;

    // Stub the Clarity queue so calls made before the script loads are buffered.
    globalThis.clarity =
        globalThis.clarity ||
        function (...args) {
            globalThis.clarity.q = globalThis.clarity.q || [];
            globalThis.clarity.q.push(args);
        };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${projectId}`;
    script.onerror = () => {
        console.error(`[Clarity] Failed to load script: ${script.src}`);
    };
    document.head.appendChild(script);
}

/**
 * Identifies the currently logged-in Atlas user in Clarity so that session
 * recordings are searchable by user ID and display a friendly name.
 * Safe to call immediately after `injectClarity` — the call is buffered in
 * the queue stub and replayed once the real script loads.
 */
export function identifyUser() {
    const userId = get(globalThis, "_CS_CONTEXT.userId");
    if (!userId || identifiedUserId === userId) return;

    const firstName = get(globalThis, "_CS_CONTEXT.firstName", "");
    const lastName = get(globalThis, "_CS_CONTEXT.lastName", "");
    const email = get(globalThis, "_CS_CONTEXT.email", "");
    const friendlyName = [firstName, lastName].filter(Boolean).join(" ") || email || undefined;

    // globalThis.clarity is guaranteed to exist (stub or real) after injectClarity().
    globalThis.clarity("identify", userId, undefined, undefined, friendlyName);
    identifiedUserId = userId;
}

/**
 * Sends a `pageType` custom tag to Clarity for the current route path.
 * Called from the router's `afterEach` hook on every SPA navigation.
 * No-op if Clarity has not been initialised (e.g. no project ID configured).
 *
 * @param {string} path  — current route path (to.path)
 */
export function setPageType(path) {
    if (!globalThis.clarity) return;
    globalThis.clarity("set", "pageType", resolvePageType(path));
}

/**
 * Entry point called from the global bootstrap and Atlas Vue plugin fallback.
 * Reads the project ID from `window._APP_CONFIG.clarityProjectId` (injected
 * server-side), injects the Clarity script, and identifies the logged-in user.
 * Silently exits when no project ID is present.
 */
export function initClarity() {
    const projectId = get(globalThis, "_APP_CONFIG.clarityProjectId");
    if (!projectId) return;

    injectClarity(projectId);
    identifyUser();
}
